import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useTellescopeInfiniteQuery, useUsersMap } from "@tellescope/tanstack-query";
import {
  compileFreeformFilterBlock,
  type FreeformFieldOption,
  type FreeformFilterBlock,
  type MongoFilter,
} from "@tellescope/utils";
import { BlockProps } from "../types";
import { useBlock } from ".";
import { i18n } from "@tellescope/i18n";

/**
 * The four selectable ticket stages, matching the tickets feature's stage vocabulary. Exposed here
 * (rather than imported from the app) so the filter popover's value select stays app-agnostic.
 */
export const getTicketStageOptions = (): Array<{ value: string; label: string }> => [
  { value: "Not Started", label: i18n.t("Not Started", { ns: "tickets" }) },
  { value: "In Progress", label: i18n.t("In Progress", { ns: "tickets" }) },
  { value: "Blocked", label: i18n.t("Blocked", { ns: "tickets" }) },
  { value: "Archived", label: i18n.t("Archived", { ns: "tickets" }) },
];

/**
 * The selectable ticket priorities, matching the tickets feature's numeric priority vocabulary
 * (High=10, Medium=5, Low=1, Not Set=0). "Not Set" (0) also matches tickets with no priority — see
 * {@link filterTickets}.
 */
export const getTicketPriorityOptions = (): Array<{ value: string; label: string }> => [
  { value: "10", label: i18n.t("High", { ns: "tickets" }) },
  { value: "5", label: i18n.t("Medium", { ns: "tickets" }) },
  { value: "1", label: i18n.t("Low", { ns: "tickets" }) },
  { value: "0", label: i18n.t("Not Set", { ns: "tickets" }) },
];

/**
 * Builds the `mdbFilter` for the tickets query: the block's compiled freeform filter, always scoped
 * to one contact. Pure and hook-free so it can be unit-tested in isolation.
 *
 * The contact scope belongs here rather than in a sibling `filter` because a request carrying both
 * ignores `filter` outright — which would widen the block to every ticket in the organization.
 *
 * A ticket with no `priority` is treated as priority 0, which is what the "Not Set" option means.
 * The stored documents leave the field off rather than writing a 0, so an `equals 0` condition is
 * rewritten to also match a missing field.
 */
export const buildTicketsMdbFilter = (compiledFilter: MongoFilter | undefined, enduserId: string): MongoFilter => {
  if (!compiledFilter) return { enduserId };

  const conditions = mapFilterConditions(compiledFilter, (field, condition) =>
    field === "priority" ? matchUnsetPriorityAsZero(condition) : { [field]: condition }
  );
  return { $and: [{ enduserId }, conditions] };
};

/**
 * "Not Set" is priority 0, but a ticket that never had one stores no `priority` field at all, so an
 * equality test against 0 has to match a missing field as well. Its negation has to exclude those
 * same tickets, which `$ne: 0` alone would keep.
 */
const matchUnsetPriorityAsZero = (condition: unknown): MongoFilter => {
  if (condition === 0) return { priority: { $in: [null, 0] } };
  if (isNotEqualToZero(condition)) return { priority: { $nin: [null, 0] } };
  return { priority: condition };
};

const isNotEqualToZero = (condition: unknown): boolean =>
  typeof condition === "object" && condition !== null && "$ne" in condition && condition.$ne === 0;

/** Rebuilds a compiled filter, replacing each leaf `field: condition` pair via `mapCondition`. */
const mapFilterConditions = (
  filter: MongoFilter,
  mapCondition: (field: string, condition: unknown) => MongoFilter
): MongoFilter =>
  Object.entries(filter).reduce<MongoFilter>((acc, [key, value]) => {
    if ((key === "$and" || key === "$or") && Array.isArray(value)) {
      return Object.assign(acc, {
        [key]: value.map((entry: MongoFilter) => mapFilterConditions(entry, mapCondition)),
      });
    }
    return Object.assign(acc, mapCondition(key, value));
  }, {});

export interface UseTicketsBlockArgs extends BlockProps {}

export const useTicketsBlock = (args: UseTicketsBlockArgs) => {
  const { t } = useTranslation("tickets");

  const block = useBlock({ ...args, type: "tickets.tickets" });

  const filterBlock = useMemo<FreeformFilterBlock>(
    () =>
      block.parsedInfo?.filter ?? {
        id: args.blockId ?? `${args.viewId}-${args.blockIndex}`,
        logic: "and",
        filters: [],
      },
    [block.parsedInfo?.filter, args.blockId, args.viewId, args.blockIndex]
  );

  const compiledFilter = useMemo(() => compileFreeformFilterBlock(filterBlock), [filterBlock]);

  const mdbFilter = useMemo(
    () => buildTicketsMdbFilter(compiledFilter, args.enduserId),
    [compiledFilter, args.enduserId]
  );

  const {
    data: tickets,
    isLoading,
    fetchNextPage,
  } = useTellescopeInfiniteQuery({
    model: "tickets",
    method: "getSome",
    request: { mdbFilter },
    enabled: !!args.enduserId,
    select: (data) => data.pages.flat(),
  });

  const ownerIds = useMemo(
    () =>
      (tickets ?? []).reduce<string[]>((acc, ticket) => {
        if (ticket.owner && !acc.includes(ticket.owner)) acc.push(ticket.owner);
        return acc;
      }, []),
    [tickets]
  );
  const usersById = useUsersMap(ownerIds);

  const filterFieldOptions = useMemo<FreeformFieldOption[]>(
    () => [
      { value: "enduserId", label: t("Patient"), valueType: "string" },
      { value: "priority", label: t("Priority"), valueType: "number", operations: ["equals", "does_not_equal"] },
      { value: "stage", label: t("Stage"), valueType: "string" },
      { value: "owner", label: t("Owner"), valueType: "tags" },
      { value: "tags", label: t("Tags"), valueType: "tags" },
    ],
    [t]
  );

  const onFilterChange = useCallback(
    (nextBlock: FreeformFilterBlock) => {
      block.updateInfo({ ...block.parsedInfo, id: block.parsedInfo?.id ?? crypto.randomUUID(), filter: nextBlock });
    },
    [block]
  );

  const tagOptions = useMemo(() => {
    const selectedTags = filterBlock.filters.flatMap((filter) =>
      filter.field === "tags" && Array.isArray(filter.value) ? filter.value : []
    );
    return [...new Set([...(tickets ?? []).flatMap((ticket) => ticket.tags ?? []), ...selectedTags])];
  }, [tickets, filterBlock.filters]);

  return {
    ...block,
    enduserId: args.enduserId,
    tickets: tickets ?? [],
    isLoading,
    fetchNextPage,
    usersById,
    filterBlock,
    filterFieldOptions,
    onFilterChange,
    tagOptions,
  };
};
